import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Camera, Loader2, Plus, Crown, Layers, Images, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { STAGES } from '../lib/data';
import { fileToCenterCroppedDataURL } from '../lib/image';
import ImagePicker from './ImagePicker';
import AiCaution from './AiCaution';
import PlantCandidateEditor from './PlantCandidateEditor';
import { candidateCommonName, candidateSpeciesKey, candidateCategory } from '../lib/plantIdentity';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const MAX_GROUPS = 25;
const MAX_SNAPS = 3;
const newGroup = () => ({ id: Math.random().toString(36).slice(2), snaps: [], candidates: null, selectedIdx: 0, bedId: '', existingPlantId: '', added: false });

function dataURLtoFile(dataUrl, name) {
    const [meta, b64] = dataUrl.split(',');
    const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/jpeg';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new File([arr], name, { type: mime });
}

export default function BatchIdentify({ onClose, onAdded }) {
    const { locale } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();
    const isPremium = !!user?.premium || !!user?.ai_payg_available; // paid AI is available without a subscription
    const [groups, setGroups] = useState([newGroup()]);
    const [flow, setFlow] = useState('new');
    const [beds, setBeds] = useState([]);
    const [plants, setPlants] = useState([]);
    const [identifying, setIdentifying] = useState(false);
    const [adding, setAdding] = useState(false);
    const [varietyCatalog, setVarietyCatalog] = useState(null);

    useEffect(() => {
        api.get('/plant-varieties').then(r => setVarietyCatalog(r.data)).catch(() => {});
        api.get('/plants').then((r) => setPlants(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        if (isPremium) api.get('/beds').then((r) => setBeds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, [isPremium]);

    if (!isPremium && flow === 'new') {
        return (
            <div className="px-5 pb-6" data-testid="batch-premium-gate">
                <div className="grid grid-cols-2 gap-2 mb-4">
                    <button className="bg-brand-green text-white rounded-2xl py-2.5 text-sm">Identify new plants</button>
                    <button onClick={() => setFlow('existing')} className="bg-brand-card border border-brand-border rounded-2xl py-2.5 text-sm text-brand-ink">Add to my plants</button>
                </div>
                <div className="bg-brand-card border border-brand-border rounded-3xl p-6 text-center space-y-3">
                    <Crown size={28} strokeWidth={1.5} className="mx-auto text-brand-ochre" />
                    <h4 className="font-serif text-lg text-brand-ink">Multi-add is a Premium feature</h4>
                    <p className="text-sm text-brand-ink2">Snap several plants one after another (tomato, chili, basil…) and identify them all in one go — each with up to 3 angles and smart center-crop.</p>
                    <button data-testid="batch-upgrade-btn" onClick={() => { onClose?.(); navigate('/premium'); }} className="bg-brand-green hover:bg-brand-greenDark text-white px-6 py-3 rounded-full font-medium">Unlock Premium</button>
                </div>
            </div>
        );
    }

    const patchGroup = (id, patch) => setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, ...patch } : g)));

    const patchCandidate = (id, index, patch) => setGroups((gs) => gs.map((g) => {
        if (g.id !== id || !g.candidates?.[index]) return g;
        const candidates = g.candidates.map((candidate, i) => (i === index ? { ...candidate, ...patch } : candidate));
        return { ...g, candidates };
    }));

    const addSnap = async (id, file) => {
        if (!file) return;
        let preview;
        try { preview = await fileToCenterCroppedDataURL(file); }
        catch { toast.error('Could not read photo'); return; }
        setGroups((gs) => gs.map((g) => (g.id === id && g.snaps.length < MAX_SNAPS ? { ...g, snaps: [...g.snaps, preview], candidates: null, selectedIdx: 0 } : g)));
    };

    const removeSnap = (id, i) => setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, snaps: g.snaps.filter((_, idx) => idx !== i), candidates: null, selectedIdx: 0 } : g)));
    const removeGroup = (id) => setGroups((gs) => (gs.length > 1 ? gs.filter((g) => g.id !== id) : gs.map((g) => (g.id === id ? newGroup() : g))));

    const addMany = async (files) => {
        const selected = Array.from(files || []).slice(0, MAX_GROUPS);
        if (!selected.length) return;
        const prepared = [];
        for (const file of selected) {
            try { prepared.push({ ...newGroup(), snaps: [await fileToCenterCroppedDataURL(file)] }); }
            catch { /* skip unreadable images */ }
        }
        if (!prepared.length) { toast.error('Could not read the selected photos'); return; }
        setGroups((current) => {
            const keep = current.filter((g) => g.snaps.length || g.candidates?.length);
            return [...keep, ...prepared].slice(0, MAX_GROUPS);
        });
        toast.success(`${prepared.length} photos ready to identify`);
    };

    const filled = groups.filter((g) => g.snaps.length > 0);

    const identifyAll = async () => {
        if (!filled.length) return;
        setIdentifying(true);
        try {
            const r = await api.post('/ai/identify-batch', {
                groups: filled.map((g) => ({ images_base64: g.snaps })),
                locale,
            });
            const results = r.data?.results || [];
            setGroups((gs) => {
                let idx = 0;
                return gs.map((g) => {
                    if (!g.snaps.length) return g;
                    const res = results[idx++];
                    const list = Array.isArray(res?.candidates) ? res.candidates : (res?.name ? [res] : []);
                    return { ...g, candidates: list.length ? list : null, selectedIdx: 0, error: res?.error || (!list.length ? 'Could not identify' : null) };
                });
            });
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 402) { toast.error(typeof d === 'string' ? d : 'Premium required'); onClose?.(); navigate('/premium'); return; }
            if (err?.response?.status === 401 || err?.response?.status === 403) return; // global signup CTA handles it
            toast.error(typeof d === 'string' ? d : 'Identification failed');
        } finally { setIdentifying(false); }
    };

    const identifyOne = async (group) => {
        if (!group.snaps.length) return;
        patchGroup(group.id, { identifying: true, error: null });
        try {
            const r = await api.post('/ai/identify', { images_base64: group.snaps, mode: 'identify', locale });
            const list = Array.isArray(r.data?.candidates) ? r.data.candidates : (r.data?.name ? [r.data] : []);
            patchGroup(group.id, { candidates: list.length ? list : null, selectedIdx: 0, error: list.length ? null : 'Could not identify', identifying: false });
        } catch (err) {
            patchGroup(group.id, { identifying: false, error: err?.response?.data?.detail || 'Could not re-identify' });
        }
    };

    const identified = groups.filter((g) => g.candidates?.length && !g.added && candidateCommonName(g.candidates[g.selectedIdx] || g.candidates[0]).trim());

    const attachable = groups.filter((g) => g.snaps.length && g.existingPlantId && !g.added);

    const attachAll = async () => {
        if (!attachable.length) return;
        setAdding(true);
        let ok = 0;
        for (const g of attachable) {
            try {
                const fd = new FormData();
                fd.append('file', dataURLtoFile(g.snaps[0], 'plant-update.jpg'));
                const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                await api.post('/journal', { plant_id: g.existingPlantId, note: '', image_path: up.data.path });
                await api.patch(`/plants/${g.existingPlantId}`, { image_path: up.data.path });
                patchGroup(g.id, { added: true });
                ok++;
            } catch (err) {
                const d = err?.response?.data?.detail;
                toast.error(typeof d === 'string' ? d : 'Could not attach photo');
            }
        }
        setAdding(false);
        if (ok) {
            toast.success(`${ok} photo${ok === 1 ? '' : 's'} added to your plants`);
            onAdded?.();
        }
        if (ok === attachable.length) onClose?.();
    };

    const addAll = async () => {
        if (!identified.length) return;
        setAdding(true);
        let ok = 0, fail = 0;
        for (const g of identified) {
            const sel = g.candidates[g.selectedIdx] || g.candidates[0];
            try {
                let image_path = null;
                try {
                    const fd = new FormData();
                    fd.append('file', dataURLtoFile(g.snaps[0], 'plant.jpg'));
                    const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    image_path = up.data.path;
                } catch { /* image optional */ }
                const r = await api.post('/plants', {
                    name: sel.display_name || sel.name || candidateCommonName(sel),
                    display_name: sel.display_name || sel.name || candidateCommonName(sel),
                    species_common_name: candidateCommonName(sel),
                    scientific_name: sel.scientific_name || undefined,
                    variety: sel.variety || undefined,
                    emoji: sel.emoji || '🌱',
                    stage: STAGES.includes(sel.stage) ? sel.stage : 'Growing',
                    species: candidateSpeciesKey(sel),
                    category: candidateCategory(sel) || undefined,
                    care: sel.care || undefined,
                    sun: sel.sun || undefined,
                    water: sel.water || undefined,
                    ai_confidence: typeof sel.confidence === 'number' ? sel.confidence : undefined,
                    water_interval_days: sel.water_interval_days || 3,
                    image_path,
                });
                if (g.bedId) {
                    try { await api.post(`/beds/${g.bedId}/place-plant`, { plant_id: r.data.id }); }
                    catch { toast.error(`${sel.name}: could not place in bed`); }
                }
                patchGroup(g.id, { added: true });
                onAdded?.(r.data);
                ok++;
            } catch (err) {
                fail++;
                const d = err?.response?.data?.detail;
                toast.error(typeof d === 'string' ? d : `Could not add ${sel.name || 'plant'}`);
                if (err?.response?.status === 402) break;
            }
        }
        setAdding(false);
        if (ok) toast.success(`${ok} plant${ok > 1 ? 's' : ''} added 🌱`);
        if (ok && !fail && groups.filter(g => g.snaps.length).every(g => g.added || identified.some(item => item.id === g.id))) onClose?.();
    };

    return (
        <fieldset disabled={adding || identifying || groups.some(g => g.identifying)} className="px-5 pb-6 space-y-4 min-w-0" data-testid="batch-identify">
            <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setFlow('new')} className={`rounded-2xl py-2.5 text-sm ${flow === 'new' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>Identify new plants</button>
                <button onClick={() => setFlow('existing')} className={`rounded-2xl py-2.5 text-sm ${flow === 'existing' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>Add to my plants</button>
            </div>
            <p className="text-sm text-brand-ink2">{flow === 'new' ? 'Snap 1-3 photos per plant, then identify them all in one go.' : 'Choose all your new photos, then connect each one to a plant already in your garden.'} Photos are compressed and focused on the centre plant.</p>
            {flow === 'new' && <AiCaution />}

            <ImagePicker testId="batch-many" multiple onSelect={addMany}>
                {(openPicker) => (
                    <button onClick={openPicker} className="w-full inline-flex items-center justify-center gap-2 bg-brand-green/10 border border-brand-green/25 text-brand-green py-3 rounded-2xl text-sm font-medium">
                        <Images size={17} /> Choose many plant photos at once
                    </button>
                )}
            </ImagePicker>

            {groups.map((g, gi) => {
                const sel = g.candidates?.[g.selectedIdx] || g.candidates?.[0];
                return (
                    <div key={g.id} data-testid={`batch-group-${gi}`} className={`bg-brand-card border rounded-3xl p-4 space-y-3 ${g.added ? 'border-brand-green/60 opacity-60' : 'border-brand-border'}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Plant {gi + 1}{g.added ? ' · added ✓' : ''}</span>
                            <button data-testid={`batch-group-remove-${gi}`} onClick={() => removeGroup(g.id)} className="p-1 hover:bg-brand-bg2 rounded-full text-brand-ink3"><X size={14} /></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {g.snaps.map((s, i) => (
                                <div key={i} className="relative">
                                    <img src={s} alt="" className="w-full aspect-square object-cover rounded-2xl border border-brand-border" />
                                    {!g.added && (
                                        <button onClick={() => removeSnap(g.id, i)} className="absolute top-1 right-1 bg-brand-ink/70 text-white p-1 rounded-full"><X size={12} /></button>
                                    )}
                                </div>
                            ))}
                            {!g.added && g.snaps.length < MAX_SNAPS && (
                                <ImagePicker testId={`batch-pick-${gi}`} onSelect={(f) => addSnap(g.id, f)}>
                                    {(openPicker) => (
                                        <button data-testid={`batch-add-photo-${gi}`} onClick={openPicker} className="aspect-square border-2 border-dashed border-brand-border rounded-2xl flex flex-col items-center justify-center text-brand-ink3 hover:border-brand-green transition-colors">
                                            <Camera size={20} strokeWidth={1.5} className="text-brand-green" />
                                            <span className="text-[10px] mt-1">{g.snaps.length}/{MAX_SNAPS}</span>
                                        </button>
                                    )}
                                </ImagePicker>
                            )}
                        </div>
                        {g.error && !g.candidates && <p className="text-xs text-red-500">{g.error}</p>}
                        {flow === 'existing' && !g.added && (
                            <select value={g.existingPlantId} onChange={(e) => patchGroup(g.id, { existingPlantId: e.target.value })} className="w-full bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-2.5 text-sm text-brand-ink">
                                <option value="">Choose an existing plant…</option>
                                {plants.map((plant) => <option key={plant.id} value={plant.id}>{plant.emoji || '🌱'} {plant.name}</option>)}
                            </select>
                        )}
                        {flow === 'new' && g.candidates?.length > 0 && (
                            <div className="space-y-2">
                                {g.candidates.length > 1 && (
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {g.candidates.map((c, i) => (
                                            <button key={i} data-testid={`batch-candidate-${gi}-${i}`} onClick={() => patchGroup(g.id, { selectedIdx: i })} className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border ${i === g.selectedIdx ? 'bg-brand-green text-white border-brand-green' : 'bg-brand-bg2 text-brand-ink border-brand-border'}`}>
                                                <span>{c.emoji || '🌱'}</span><span className="truncate max-w-[100px]">{c.name}</span>
                                                {typeof c.confidence === 'number' && <span className="text-[10px] opacity-70">{c.confidence}%</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-serif text-lg text-brand-ink">{sel?.emoji || '🌱'} {sel?.name}</div>
                                        {(sel?.scientific_name || sel?.species) && <p className="text-xs italic text-brand-ink3">{sel.scientific_name || sel.species}</p>}
                                    </div>
                                    {typeof sel?.confidence === 'number' && <span className="text-[10px] uppercase tracking-widest font-semibold bg-brand-sage/20 text-brand-green px-2 py-1 rounded-full">{sel.confidence}%</span>}
                                </div>
                                {!g.added && (
                                    <PlantCandidateEditor key={`${g.id}-${g.selectedIdx}`} candidate={sel} varietyCatalog={varietyCatalog}
                                        onChange={patch => patchCandidate(g.id, g.selectedIdx, patch)} />
                                )}
                                {!g.added && (
                                    <button onClick={() => identifyOne(g)} disabled={g.identifying} className="inline-flex items-center gap-1.5 text-xs text-brand-green hover:text-brand-greenDark disabled:opacity-50">
                                        {g.identifying ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Re-identify only this plant
                                    </button>
                                )}
                                {!g.added && beds.length > 0 && (
                                    <select data-testid={`batch-bed-select-${gi}`} value={g.bedId} onChange={(e) => patchGroup(g.id, { bedId: e.target.value })} className="w-full bg-brand-bg2 border border-brand-border rounded-2xl px-3 py-2.5 text-sm">
                                        <option value="">My garden only</option>
                                        {beds.map((b) => <option key={b.id} value={b.id}>Also place in bed: {b.name || 'Bed'}</option>)}
                                    </select>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {groups.length < MAX_GROUPS && (
                <button data-testid="batch-add-group" onClick={() => setGroups((gs) => [...gs, newGroup()])} className="w-full inline-flex items-center justify-center gap-2 bg-brand-bg2 hover:bg-brand-border text-brand-ink py-3 rounded-full text-sm font-medium">
                    <Plus size={15} /> Next plant
                </button>
            )}

            <div className="flex gap-2">
                {flow === 'new' ? <button data-testid="batch-identify-all" onClick={identifyAll} disabled={identifying || adding || !filled.length} className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {identifying ? <><Loader2 size={15} className="animate-spin" /> Analyzing {filled.length}…</> : <><Sparkles size={15} /> Identify all ({filled.length})</>}
                </button> : <button onClick={attachAll} disabled={adding || !attachable.length} className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {adding ? <><Loader2 size={15} className="animate-spin" /> Adding…</> : <><Images size={15} /> Add selected ({attachable.length})</>}
                </button>}
                {flow === 'new' && identified.length > 0 && (
                    <button data-testid="batch-add-all" onClick={addAll} disabled={adding || identifying} className="flex-1 bg-brand-ochre hover:opacity-90 text-white py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {adding ? <><Loader2 size={15} className="animate-spin" /> Adding…</> : <><Layers size={15} /> Add all ({identified.length})</>}
                    </button>
                )}
            </div>
        </fieldset>
    );
}
